import { CORE_FONT } from "@/lib/theme/core";

/**
 * The shared advertising band.
 *
 * QA filed the same complaint on five pages — "[Tablet] 兩側的空白太多",
 * "廣告位置會有大量留白", "太多留白空間" — wherever a 300px creative sits in a
 * sidebar that `SEGMENT_GRID` has already collapsed to full width. Widening the
 * creative is not an option: these are fixed-size IAB units (300×250, 300×600)
 * and stretching a 300px raster to 721px would just blur it.
 *
 * The fix is the SCMP treatment: keep the creative at its native size and give
 * the slot a tinted band, so the space either side reads as the ad container
 * rather than as an accidental gap. The label sits inside the band above the
 * creative. Where the creative already fills its column (the lg sidebar) the
 * band collapses to a thin backing behind label + image and changes nothing.
 *
 * Colour comes from `--hkej-ad-band` so a vertical can retune it from its
 * layout — the same mechanism as `--hkej-hover`. The default suits every
 * light surface in the product.
 */
export function AdBand({
  label = "廣告",
  showLabel = true,
  children,
  className = "",
}: {
  label?: string;
  /** Suppress the caption for slots whose creative carries its own. */
  showLabel?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      data-ad-band=""
      className={`flex w-full flex-col items-center ${className}`}
      style={{ backgroundColor: "var(--hkej-ad-band, #f7f7f7)" }}
    >
      {showLabel && (
        // The only padding in the band. The creative itself sits flush — any
        // padding around it would inset the image and read as a margin rather
        // than as the slot's own background.
        <span
          className="block w-full text-center"
          style={{
            color: "var(--hkej-ad-label, #767676)",
            fontFamily: CORE_FONT.sans,
            fontSize: 11,
            lineHeight: "normal",
            letterSpacing: "0.12em",
            fontWeight: "var(--hkej-ad-label-weight, 400)",
            padding: "6px 0",
          }}
        >
          {label}
        </span>
      )}
      {children}
    </div>
  );
}
